import { forwardRef, useContext, useEffect } from 'react';
import { Field, type FieldProps } from 'formik';
import isEmpty from 'lodash/isEmpty';

import { FormContext, isRequiredField } from './utils';
import SimpleSelectInput from './SimpleSelectInput';
import type { SimpleSelectInputProps } from './SimpleSelectInput';

/**
 * Собственные пропсы Formik-обёртки одиночного селекта.
 *
 * То, что SelectInput достаивает из `{...props}` и обрабатывает сам, а НЕ пробрасывает
 * в SimpleSelectInput как есть. Остальные пропсы (`value`, `onChange`, `error`, `label`,
 * `data`, ...) наследуются из {@link SimpleSelectInputProps} — ниже в {@link SelectInputProps}.
 *
 * `value`/`onChange`/`error` здесь НЕ повторяются — они уже объявлены в SimpleSelectInputProps
 * и концептуально «перехватываются» SelectInput'ом (в деструктуризации вытаскиваются отдельно
 * от `...rest`), но типизируются тем же типом, что и у Simple — конфликта пересечения нет.
 */
export interface SelectInputOwnProps {
  /** Ручной признак обязательности; иначе автодетект по validationSchema (isRequiredField). */
  required?: boolean;
  /** Yup-схема формы; берётся из FormContext, если не передана явно. */
  validationSchema?: any;
  /**
   * Колбэк вызывается, когда поле переходит в состояние ошибки валидации.
   * Получает текст ошибки (`meta.error ?? error`).
   */
  onValidateFail?: (error: unknown) => void;
}

/**
 * Пропсы Formik-обёртки одиночного селекта (SelectInput).
 *
 * Расширяет {@link SimpleSelectInputProps} своими Formik-специфичными полями.
 * `field`/`meta`/`form` инжектируются render-prop'ом `<Field>` во внутренний компонент
 * и НЕ входят в этот тип — внешний потребитель их не передаёт.
 *
 * Селекторы получают к этому типу доступ через `ComponentProps<typeof SelectInput>`.
 */
export type SelectInputProps = SimpleSelectInputProps & SelectInputOwnProps;

/** Внутренние пропсы: {@link SelectInputProps} + `field`/`meta`/`form` из formik `<Field>`. */
type SelectInputInnerProps = SelectInputProps & FieldProps;

const SelectInput = forwardRef<HTMLElement, SelectInputInnerProps>((props, ref) => {
  const {
    field,
    form,
    meta,
    value,
    required,
    validationSchema,
    onValidateFail,
    onChange,
    error,
    ...rest
  } = props;

  const {
    submitCount,
    setFieldValue,
    setFieldTouched,
  } = form;

  // Formik не всегда затирает Yup-required ошибку ('Укажите…') после того, как поле уже заполнено.
  // Раньше это лечилось прямой мутацией read-only meta.error в render-prop Field (варнинг в strict mode).
  // Здесь — подавляем такую «устаревшую» required-ошибку чистым вычислением, не трогая meta.
  const isFilled = field.value !== undefined && field.value !== null && field.value !== '';
  const isStaleRequiredError = Boolean(
    isFilled && meta.error && typeof meta.error === 'string' && meta.error.includes('Укажите')
  );
  const effectiveError = isStaleRequiredError ? null : (meta.error ?? error);

  const hasError = !isEmpty(effectiveError)
    && ((meta.touched && isFilled) || (submitCount > 0));

  const handleChange = (event: any) => {
    setFieldValue(field.name, event?.target?.value ?? null);

    if (typeof onChange === 'function') {
      onChange(event);
    }
  };

  useEffect(() => {
    if (value && (value !== field.value)) {
      setFieldValue(field.name, value ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!meta.touched) {
      setFieldTouched(field.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  useEffect(() => {
    if (hasError && (typeof onValidateFail === 'function')) {
      onValidateFail(effectiveError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  return (
    <SimpleSelectInput
      { ...field }
      { ...rest }
      ref={ ref }
      value={ field.value }
      error={ hasError ? effectiveError : null }
      required={ required || isRequiredField(validationSchema, field.name) }
      onChange={ handleChange }
    />
  );
});

const SelectInputWrapper = forwardRef<HTMLElement, SelectInputProps>((props, ref) => {
  // FormContext создаётся в utils.js (createContext() без дженерика); при allowJs:false
  // useContext возвращает unknown. Приводим к ожидаемой форме контекста формы
  // (Provider всегда подаёт значение; default — undefined, деструктуризация безопасна).
  const { validationSchema } = useContext(FormContext) as { validationSchema?: any };

  return (
    <Field { ...props }>
      { fieldProps => (
        <SelectInput
          ref={ ref }
          validationSchema={ validationSchema }
          { ...fieldProps }
          { ...props }
        />
      ) }
    </Field>
  );
});

SelectInputWrapper.displayName = 'SelectInput';

export default SelectInputWrapper;
